// Tenant POS printer registry (pos-network-printing spec, Layer 2).
//
// Mirrors admin-mobile-app/src/utils/printerRegistry.js. Keep the two byte-identical apart
// from the one header line naming the mirror direction, following the existing
// money.js / paperSize.js / storeTicket.js duplication convention.
//
// WHAT THIS IS. A tenant lists the restaurant's network printers once, in General Settings:
// each is { id, name, ip, port, role }. `role` is 'kitchen' or 'cashier'. The kitchen ticket
// routes to a kitchen printer and the itemized receipt to a cashier printer (see
// printRouting.js). IPs live here, tenant-level, NOT in per-device keychains, so changing a
// printer's address is one edit.
//
// WHY A SHARED NORMALIZER. The same list is read by /admin/general (the settings screens)
// and by the POS queue response (a POS-only cashier, whose /admin/general 403s). If the two
// coerced it differently, the same tenant would route to different printers depending on
// which response the client happened to read. Same discipline as paperSize: one allowlist,
// mirrored to the clients and vendored to the lambdas (paper_size.py-style
// printer_registry.py), with a cross-language parity test.
//
// TOTAL AND DEFENSIVE. Runs on the order path (routing an auto-print) and inside settings
// normalization. A malformed entry is DROPPED, never throws — a bad printer row must not
// blank a cashier's queue or lose the whole list.
//
// Pure: no React, no native, no network.

import { LANGUAGE_REGISTRY, DEFAULT_LANGUAGE } from './i18nRegistry';

export const PRINTER_ROLES = ['kitchen', 'cashier'];
export const DEFAULT_PRINTER_PORT = 9100;

/**
 * normalizeLanguage(raw) -> a supported language code, defaulting to English.
 *
 * The language a printer's tickets are printed in. Sourced from LANGUAGE_REGISTRY so the
 * printer dropdown and the print builders agree on the code set, and so adding a language is
 * one edit in i18nRegistry (mirrored to the backend). An unknown or missing value resolves
 * to DEFAULT_LANGUAGE ('en') — English is the safe default and matches every legacy row that
 * predates this field.
 */
export function normalizeLanguage(raw) {
  const code = String(raw == null ? '' : raw).trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(LANGUAGE_REGISTRY, code) ? code : DEFAULT_LANGUAGE;
}

// A restaurant does not have dozens of printers; a cap stops a malformed or hostile payload
// from ballooning the settings row.
export const MAX_PRINTERS = 20;

const NAME_MAX = 40;
const ID_MAX = 40;
const HOST_MAX = 120;

/**
 * normalizeHost(raw) -> a trimmed mDNS/DNS host string, or '' when unusable.
 *
 * The OPTIONAL stable name a printer advertises over Bonjour (e.g. "Star-TSP143.local.").
 * Purely additive: `ip` is still the required address a print connects to. `host` is only
 * kept so the app can RE-RESOLVE the ip when a DHCP lease changes the address — the printer
 * keeps its name across reboots even though its ip may move. Never validated as an IP; it is
 * a passthrough label. Empty string means "no stable name known", which disables re-resolve
 * and leaves the ip-only behaviour unchanged.
 */
export function normalizeHost(raw) {
  // Only a real string is a host. A number/object is not a name a user typed or a scan
  // returned, so it is rejected rather than coerced (String(5) would sneak "5" through).
  if (typeof raw !== 'string') return '';
  const s = raw.trim().slice(0, HOST_MAX);
  // A host must look like a hostname (letters/digits/dot/hyphen/underscore). Anything else
  // is dropped rather than stored, so a garbage value cannot be fed to the socket later.
  if (!s) return '';
  return /^[A-Za-z0-9._-]+$/.test(s) ? s : '';
}

/**
 * isValidIpv4(s) -> boolean
 *
 * Plain dotted-quad only. Deliberately NOT hostnames: a printer on the LAN is configured by
 * IP (its self-test slip prints one), and accepting hostnames would invite a value that
 * resolves differently per device or not at all. No CIDR, no port suffix — the port is its
 * own field.
 */
export function isValidIpv4(s) {
  const str = typeof s === 'string' ? s.trim() : '';
  const m = str.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!m) return false;
  return m.slice(1).every((oct) => {
    const n = Number(oct);
    // Reject leading zeros ("01") so a copy-pasted value cannot be silently reinterpreted.
    return String(n) === oct && n >= 0 && n <= 255;
  });
}

/** Coerce a port to 1..65535, defaulting to 9100 for anything unusable. */
export function normalizePort(raw) {
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1 || n > 65535) return DEFAULT_PRINTER_PORT;
  return n;
}

/** Coerce a role to a known value, or null when unrecognized. */
export function normalizeRole(raw) {
  const s = String(raw == null ? '' : raw).trim().toLowerCase();
  return PRINTER_ROLES.includes(s) ? s : null;
}

/**
 * normalizePrinter(raw) -> a clean printer object, or null when the entry cannot stand.
 *
 * An entry is dropped (null) only when it lacks the two things a print is impossible
 * without: a valid IP and a known role. A missing name is filled from the IP so the UI
 * always has something to show; a bad port falls back to 9100.
 */
export function normalizePrinter(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const ip = typeof raw.ip === 'string' ? raw.ip.trim() : '';
  if (!isValidIpv4(ip)) return null;
  const role = normalizeRole(raw.role);
  if (!role) return null;

  const port = normalizePort(raw.port);
  let name = String(raw.name == null ? '' : raw.name).trim().slice(0, NAME_MAX);
  if (!name) name = `${ip}:${port}`;
  let id = String(raw.id == null ? '' : raw.id).trim().slice(0, ID_MAX);
  if (!id) id = `${role}-${ip}-${port}`; // stable, deterministic from its own fields

  // Optional stable Bonjour host, used only for re-resolving a changed ip. Omitted from the
  // object entirely when unknown, so existing entries are byte-for-byte unchanged.
  const host = normalizeHost(raw.host);
  // The ticket print language. Always present and always valid ('en' when absent), so every
  // stored row carries an explicit choice and a legacy row (no language) reads as English.
  const language = normalizeLanguage(raw.language);
  const out = { id, name, ip, port, role, language };
  if (host) out.host = host;
  return out;
}

/**
 * normalizePrinters(rawList) -> a clean array (possibly empty).
 *
 * Drops malformed entries, de-duplicates by id (last wins, matching a settings-form save),
 * and caps the count. An empty result is the "no printers configured" state, which every
 * consumer treats as "fall back to today's behaviour" — that is the non-regression contract.
 */
export function normalizePrinters(rawList) {
  const list = Array.isArray(rawList) ? rawList : [];
  const byId = new Map();
  for (const raw of list) {
    const p = normalizePrinter(raw);
    if (p) byId.set(p.id, p);
    if (byId.size >= MAX_PRINTERS) break;
  }
  return Array.from(byId.values());
}

/**
 * printersForRole(printers, role) -> the configured printers of that role.
 *
 * Used by routing: "which printer does a kitchen ticket go to." Returns [] when none, which
 * routing treats as "no destination for this document, do not auto-print it here."
 */
export function printersForRole(printers, role) {
  const wanted = normalizeRole(role);
  if (!wanted) return [];
  return normalizePrinters(printers).filter((p) => p.role === wanted);
}

/**
 * hasAnyPrinter(printers) -> boolean. The opt-in signal: false means this tenant has not
 * configured network printing, so every consumer must behave exactly as before this feature.
 */
export function hasAnyPrinter(printers) {
  return normalizePrinters(printers).length > 0;
}
